import { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, message, Tag, Space, Popconfirm, Switch, Typography, Select, Alert, Tooltip } from 'antd';
import { SearchOutlined, ReloadOutlined, ExclamationCircleOutlined, KeyOutlined, DeleteOutlined, SafetyCertificateOutlined, DollarOutlined, HistoryOutlined, PauseOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { userAPI, affiliateAPI, adminAPI } from '../services/api';
import dayjs from 'dayjs';
import { Resizable } from 'react-resizable';
import { useNavigate } from 'react-router-dom';
import 'react-resizable/css/styles.css';

const { Text } = Typography;

// 可调整宽度的表头组件
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          style={{
            position: 'absolute',
            right: -5,
            bottom: 0,
            width: 10,
            height: '100%',
            cursor: 'col-resize',
            zIndex: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [searchRealnameStatus, setSearchRealnameStatus] = useState('');
  const [balanceModalVisible, setBalanceModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [balanceForm] = Form.useForm();
  const [adjustmentUser, setAdjustmentUser] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [adjustmentTotal, setAdjustmentTotal] = useState(0);
  const [adjustmentPage, setAdjustmentPage] = useState(1);
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);
  const fetchAdjustments = async (user, current = 1) => {
    setAdjustmentLoading(true);
    try {
      const res = await userAPI.getBalanceAdjustments(user.id, current, 10);
      if (res.code !== 0) throw new Error(res.message);
      setAdjustments(res.data.list || []);
      setAdjustmentTotal(res.data.total || 0);
      setAdjustmentPage(current);
    } catch { message.error('获取余额调整记录失败'); }
    finally { setAdjustmentLoading(false); }
  };
  const openAdjustments = (user) => {
    setAdjustmentUser(user);
    setAdjustments([]);
    setAdjustmentTotal(0);
    setAdjustmentPage(1);
    fetchAdjustments(user);
  };
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  // 用户备用模型配置相关状态
  const [fallbackModalVisible, setFallbackModalVisible] = useState(false);
  // 使用与用户工作台一致的模型类型分类：text/image/embeddings/audio/video/rerank/speech/transcription
  const [fallbackModels, setFallbackModels] = useState({ text: [], image: [], embeddings: [], audio: [], video: [], rerank: [], speech: [], transcription: [] });
  const [availableModels, setAvailableModels] = useState([]);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [selectedModelType, setSelectedModelType] = useState('text'); // 当前选中的模型类型
  
  // 列宽状态
  const [columns, setColumns] = useState([
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email) => email || <span style={{ color: '#999' }}>未绑定</span>,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
      render: (phone) => phone || <span style={{ color: '#999' }}>未绑定</span>,
    },
    {
      title: 'Key数量',
      dataIndex: 'api_key_count',
      key: 'api_key_count',
      width: 100,
      sorter: true,
      render: (count) => (
        <Tag color={count > 0 ? 'blue' : 'default'}>
          {count || 0} 个
        </Tag>
      ),
    },
    {
      title: '余额',
      dataIndex: 'balance',
      key: 'balance',
      width: 100,
      sorter: true,
    },
    {
      title: '商户',
      dataIndex: 'is_merchant',
      key: 'is_merchant',
      width: 70,
      render: (is_merchant, record) => (
        <Tag color={is_merchant ? 'green' : 'default'}>
          {is_merchant ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '实名认证',
      dataIndex: 'real_name_status',
      key: 'real_name_status',
      width: 100,
      render: (status, record) => {
        if (status === 'approved') {
          const isEnterprise = record.real_name_auth_type === 'enterprise';
          return <Tag color={isEnterprise ? 'blue' : 'green'}>{isEnterprise ? '企业认证' : '个人认证'}</Tag>;
        }
        const statusMap = {
          pending: { color: 'orange', text: '审核中' },
          rejected: { color: 'red', text: '未通过' },
          not_submitted: { color: 'default', text: '未认证' },
        };
        const config = statusMap[status] || { color: 'default', text: '未认证' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: true,
    },
    {
      title: '备用模型',
      key: 'fallback',
      width: 90,
      render: (_, record) => {
        try {
          const fb = record.fallback_models ? JSON.parse(record.fallback_models) : {};
          const hasFallback = Object.values(fb).some(v => v && v.length > 0);
          return hasFallback ? (
            <Tag color="orange">已配置</Tag>
          ) : (
            <Text type="secondary">无</Text>
          );
        } catch (e) {
          return <Text type="secondary">无</Text>;
        }
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
    },
  ]);

  // 处理列宽调整
  const handleResize = (index) => (_, { size }) => {
    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      width: size.width,
    };
    setColumns(newColumns);
  };

  // 合并列宽调整功能到columns
  const mergedColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, sortBy, sortOrder]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchEmail) {
        params.email = searchEmail;
      }
      if (searchPhone) {
        params.phone = searchPhone;
      }
      if (searchUserId) {
        params.user_id = searchUserId;
      }
      if (searchRealnameStatus) {
        params.realname_status = searchRealnameStatus;
      }
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = sortOrder;
      }
      const response = await userAPI.getList(page, pageSize, params);
      if (response.code === 0) {
        setUsers(response.data.list || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, sortBy, sortOrder]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.field) {
      setSortBy(sorter.field);
      setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    } else {
      setSortBy('');
      setSortOrder('');
    }
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleSearch = () => {
    setPage(1);
    fetchUsers();
  };

  const handleReset = () => {
    setSearchEmail('');
    setSearchPhone('');
    setSearchUserId('');
    setSearchRealnameStatus('');
    setPage(1);
    fetchUsers();
  };

  const handleAdjustBalance = (user) => {
    setSelectedUser(user);
    balanceForm.resetFields();
    setBalanceModalVisible(true);
  };

  const handleBalanceSubmit = async (values) => {
    try {
      const response = await userAPI.adjustBalance(
        selectedUser.id, 
        values.amount, 
        values.reason
      );
      if (response.code === 0) {
        message.success('余额调整成功');
        setBalanceModalVisible(false);
        fetchUsers();
      } else {
        message.error(response.message || '余额调整失败');
      }
    } catch (error) {
      message.error('余额调整失败');
    }
  };

  const handleUpdateStatus = async (userId, status) => {
    try {
      const response = await userAPI.updateStatus(userId, status);
      if (response.code === 0) {
        message.success('状态更新成功');
        fetchUsers();
      } else {
        message.error(response.message || '状态更新失败');
      }
    } catch (error) {
      message.error('状态更新失败');
    }
  };

  // 设置/取消商户
  const handleToggleAffiliate = async (user) => {
    const newStatus = !user.is_affiliate;
    
    // 关闭代理时显示确认弹窗
    if (!newStatus) {
      Modal.confirm({
        title: '确认取消代理资格',
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            <p>取消代理资格将执行以下操作：</p>
            <ul style={{ paddingLeft: 20, marginTop: 8 }}>
              <li>删除该用户的代理商记录</li>
              <li>清除推广码</li>
              <li>该用户将从代理商列表中移除</li>
            </ul>
            <p style={{ marginTop: 8, color: '#ff4d4f' }}>此操作不可恢复，确定继续吗？</p>
          </div>
        ),
        okText: '确认取消',
        okType: 'danger',
        cancelText: '返回',
        onOk: async () => {
          await doToggleAffiliate(user.id, newStatus);
        },
      });
    } else {
      // 开启代理直接执行
      await doToggleAffiliate(user.id, newStatus);
    }
  };

  // 设置/取消商户
  const handleToggleMerchant = async (user) => {
    const newStatus = !user.is_merchant;
    
    if (!newStatus) {
      Modal.confirm({
        title: '确认取消商户资格',
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            <p>取消商户资格后，该用户将无法再享受拉新用户的佣金提成。</p>
            <p style={{ marginTop: 8, color: '#ff4d4f' }}>确定继续吗？</p>
          </div>
        ),
        okText: '确认取消',
        okType: 'danger',
        cancelText: '返回',
        onOk: async () => {
          await doToggleMerchant(user.id, newStatus);
        },
      });
    } else {
      // 开启商户时，显示佣金比例设置
      let formValues = { commission_rate: 0.15 };

      Modal.confirm({
        title: '设置商户',
        icon: <ExclamationCircleOutlined />,
        content: (
          <Form layout="vertical" initialValues={formValues}
            onValuesChange={(changedValues) => { formValues = { ...formValues, ...changedValues }; }}
          >
            <Form.Item label="佣金比例" name="commission_rate" extra="用户消费时，商户按此比例获得佣金">
              <InputNumber min={0.01} max={1} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <div style={{ marginTop: 8 }}>
              <p>设置为商户后，将享受下线消费的佣金提成。</p>
              <p style={{ color: '#888', fontSize: 12 }}>商户是否参与平台拉新活动可在"活动中心"中设置</p>
            </div>
          </Form>
        ),
        okText: '确认设置',
        cancelText: '取消',
        onOk: async () => {
          await doToggleMerchant(user.id, newStatus, formValues.commission_rate);
        },
      });
    }
  };

  // 执行代理状态切换
  const doToggleAffiliate = async (userId, newStatus) => {
    try {
      const response = await affiliateAPI.setAffiliate(userId, {
        is_affiliate: newStatus,
      });
      if (response.code === 0) {
        message.success(newStatus ? '已设为代理' : '已取消代理');
        fetchUsers();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 执行商户状态切换
  const doToggleMerchant = async (userId, newStatus, commissionRate = 0.15) => {
    try {
      const response = await affiliateAPI.setMerchant(userId, {
        is_merchant: newStatus,
        commission_rate: commissionRate,
      });
      if (response.code === 0) {
        message.success(newStatus ? '已设为商户' : '已取消商户');
        fetchUsers();
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  // 删除用户
  const handleDeleteUser = (user) => {
    Modal.confirm({
      title: '确认删除用户',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定删除用户 <strong>{user.email}</strong> 吗？</p>
          <p style={{ marginTop: 8, color: '#ff4d4f' }}>此操作将级联删除以下数据：</p>
          <ul style={{ paddingLeft: 20, color: '#888', fontSize: 12 }}>
            <li>API Keys</li>
            <li>API Key 使用记录</li>
            <li>充值订单</li>
            <li>余额调整记录</li>
            <li>代理商信息（含佣金、提现记录）</li>
            <li>邀请返利记录</li>
            <li>问题反馈</li>
          </ul>
          <p style={{ marginTop: 8, color: '#ff4d4f', fontWeight: 'bold' }}>此操作不可恢复，确定继续吗？</p>
        </div>
      ),
      okText: '确认删除',
      okType: 'danger',
      cancelText: '返回',
      onOk: async () => {
        try {
          const response = await userAPI.deleteUser(user.id);
          if (response.code === 0) {
            message.success('用户已删除');
            fetchUsers();
          } else {
            message.error(response.message || '删除失败');
          }
        } catch (error) {
          message.error('删除用户失败');
        }
      },
    });
  };

  // 打开用户备用模型配置弹窗
  const handleOpenFallbackModal = async (user) => {
    setSelectedUser(user);
    setFallbackModalVisible(true);
    setFallbackLoading(true);
    setSelectedModelType('text'); // 重置为默认类型

    try {
      // 获取当前用户的备用模型配置
      const userFallbackRes = await adminAPI.getUserFallback(user.id);
      if (userFallbackRes.data?.fallback_models) {
        // 确保所有类型都有值
        const existingModels = userFallbackRes.data.fallback_models;
        setFallbackModels({
          text: existingModels.text || [],
          image: existingModels.image || [],
          embeddings: existingModels.embeddings || [],
          audio: existingModels.audio || [],
          video: existingModels.video || [],
          rerank: existingModels.rerank || [],
          speech: existingModels.speech || [],
          transcription: existingModels.transcription || [],
        });
      } else {
        setFallbackModels({ text: [], image: [], embeddings: [], audio: [], video: [], rerank: [], speech: [], transcription: [] });
      }

      // 获取可用的模型列表
      const modelsRes = await adminAPI.getModelConfigs();
      if (modelsRes.code === 0) {
        setAvailableModels(modelsRes.data?.list || []);
      }
    } catch (error) {
      console.error('Failed to fetch fallback config:', error);
      message.error('获取备用模型配置失败');
    } finally {
      setFallbackLoading(false);
    }
  };

  // 保存用户备用模型配置
  const handleSaveFallback = async () => {
    if (!selectedUser) return;

    try {
      const fallbackJSON = JSON.stringify(fallbackModels);
      await adminAPI.updateUserFallback(selectedUser.id, fallbackJSON);
      message.success('备用模型配置已保存');
      setFallbackModalVisible(false);
    } catch (error) {
      console.error('Failed to save fallback config:', error);
      message.error('保存备用模型配置失败');
    }
  };

  // 清除用户备用模型配置（使用全局默认）
  const handleClearFallback = async () => {
    if (!selectedUser) return;

    try {
      await adminAPI.updateUserFallback(selectedUser.id, '');
      message.success('已清除用户备用模型配置，将使用平台默认配置');
      setFallbackModalVisible(false);
    } catch (error) {
      console.error('Failed to clear fallback config:', error);
      message.error('清除备用模型配置失败');
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      active: { color: 'green', text: '正常' },
      suspended: { color: 'orange', text: '暂停' },
      deleted: { color: 'red', text: '已删除' },
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getRealNameStatusTag = (status, authType) => {
    if (status === 'approved') {
      const isEnterprise = authType === 'enterprise';
      return <Tag color={isEnterprise ? 'blue' : 'green'}>{isEnterprise ? '企业认证' : '个人认证'}</Tag>;
    }
    const statusMap = {
      pending: { color: 'orange', text: '审核中' },
      rejected: { color: 'red', text: '未通过' },
      not_submitted: { color: 'default', text: '未认证' },
    };
    const config = statusMap[status] || { color: 'default', text: '未知' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 列渲染函数
  const renderCell = (column, record) => {
    switch (column.key) {
      case 'id':
        return record.id;
      case 'email':
        return record.email || <span style={{ color: '#999' }}>未绑定</span>;
      case 'phone':
        return record.phone || <span style={{ color: '#999' }}>未绑定</span>;
      case 'api_key_count':
        return (
          <Tag color={(record.api_key_count || 0) > 0 ? 'blue' : 'default'}>
            {record.api_key_count || 0} 个
          </Tag>
        );
      case 'balance':
        return `¥${parseFloat(record.balance || 0).toFixed(6)}`;
      case 'is_merchant':
        return (
          <Switch
            checked={record.is_merchant}
            onChange={() => handleToggleMerchant(record)}
            checkedChildren="是"
            unCheckedChildren="否"
            size="small"
            style={{ backgroundColor: record.is_merchant ? '#52c41a' : undefined }}
          />
        );
      case 'real_name_status':
        return getRealNameStatusTag(record.real_name_status, record.real_name_auth_type);
      case 'status':
        return getStatusTag(record.status);
      case 'created_at':
        return dayjs(record.created_at).format('YYYY-MM-DD HH:mm');
      case 'register_ip':
        return record.register_ip || '-';
      case 'action':
        return (
          <Space size={4}>
            <Tooltip title="查看Keys">
              <Button type="text" size="small" icon={<KeyOutlined />} onClick={() => navigate(`/api-keys?user_id=${record.id}`)} />
            </Tooltip>
            <Tooltip title="调整余额">
              <Button type="text" size="small" icon={<DollarOutlined />} onClick={() => handleAdjustBalance(record)} />
            </Tooltip>
            <Tooltip title="余额调整记录">
              <Button type="text" size="small" icon={<HistoryOutlined />} onClick={() => openAdjustments(record)} />
            </Tooltip>
            <Tooltip title="备用模型">
              <Button type="text" size="small" icon={<SafetyCertificateOutlined />} onClick={() => handleOpenFallbackModal(record)} />
            </Tooltip>
            {record.status === 'active' ? (
              <Tooltip title="暂停用户">
                <Popconfirm title="确定暂停该用户吗？" onConfirm={() => handleUpdateStatus(record.id, 'suspended')}>
                  <Button type="text" size="small" danger icon={<PauseOutlined />} />
                </Popconfirm>
              </Tooltip>
            ) : (
              <Tooltip title="激活用户">
                <Button type="text" size="small" icon={<PlayCircleOutlined />} onClick={() => handleUpdateStatus(record.id, 'active')} />
              </Tooltip>
            )}
            <Tooltip title="删除用户">
              <Popconfirm title="确定删除用户吗？" onConfirm={() => handleDeleteUser(record)}>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      default:
        return record[column.dataIndex];
    }
  };

  // 带渲染函数的列配置
  const columnsWithRender = mergedColumns.map(col => ({
    ...col,
    render: col.key === 'action' 
      ? (_, record) => renderCell(col, record)
      : (_, record) => renderCell(col, record),
  }));

  return (
    <div className="fit-screen-page">
      {/* 功能区 */}
      <div className="page-header">
        <h2 className="page-title">用户管理</h2>
      </div>

      {/* 搜索栏 */}
      <div className="search-bar" style={{ display: 'flex', gap: 8 }}>
        <Input
          placeholder="搜索邮箱"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          style={{ width: 220 }}
          prefix={<SearchOutlined />}
          onPressEnter={handleSearch}
        />
        <Input
          placeholder="搜索手机号"
          value={searchPhone}
          onChange={(e) => setSearchPhone(e.target.value)}
          style={{ width: 160 }}
          prefix={<SearchOutlined />}
          onPressEnter={handleSearch}
        />
        <Input
          placeholder="搜索用户ID"
          value={searchUserId}
          onChange={(e) => setSearchUserId(e.target.value)}
          style={{ width: 130 }}
          prefix={<SearchOutlined />}
          onPressEnter={handleSearch}
        />
        <Select
          placeholder="实名状态"
          value={searchRealnameStatus || undefined}
          onChange={(v) => { setSearchRealnameStatus(v || ''); setPage(1); setTimeout(handleSearch, 0); }}
          allowClear
          style={{ width: 130 }}
        >
          <Select.Option value="approved">已认证</Select.Option>
          <Select.Option value="pending">审核中</Select.Option>
          <Select.Option value="rejected">未通过</Select.Option>
          <Select.Option value="not_submitted">未认证</Select.Option>
        </Select>
        <Button type="primary" onClick={handleSearch}>
          搜索
        </Button>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>
          重置
        </Button>
      </div>

      {/* 表格容器 */}
      <div className="table-container">
        <Table
          variant="bordered"
          columns={columnsWithRender}
          dataSource={users}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content', y: 600 }}
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
          onChange={handleTableChange}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </div>

      <Modal title={`余额调整记录 · ${adjustmentUser?.email || adjustmentUser?.id || ''}`}
        open={!!adjustmentUser} onCancel={() => setAdjustmentUser(null)} footer={null} width={1000}>
        <Table rowKey="id" loading={adjustmentLoading} dataSource={adjustments} scroll={{ x: 850 }}
          pagination={{ current: adjustmentPage, pageSize: 10, total: adjustmentTotal, showSizeChanger: false,
            onChange: (current) => fetchAdjustments(adjustmentUser, current), showTotal: (n) => `共 ${n} 条记录` }}
          columns={[
            { title: '调整时间', dataIndex: 'created_at', width: 175, render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm:ss') },
            { title: '调整金额（元）', dataIndex: 'amount', width: 140, render: (v) => <Text type={Number(v) < 0 ? 'danger' : 'success'}>{Number(v) > 0 ? '+' : ''}{Number(v).toFixed(6)}</Text> },
            { title: '调整前余额（元）', dataIndex: 'balance_before', width: 150, render: (v) => Number(v).toFixed(6) },
            { title: '调整后余额（元）', dataIndex: 'balance_after', width: 150, render: (v) => Number(v).toFixed(6) },
            { title: '调整原因', dataIndex: 'reason', render: (v) => <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{v || '未填写'}</span> },
          ]} />
      </Modal>

      {/* 调整余额对话框 */}
      <Modal
        title="调整余额"
        open={balanceModalVisible}
        onCancel={() => setBalanceModalVisible(false)}
        footer={null}
      >
        <Form
          form={balanceForm}
          onFinish={handleBalanceSubmit}
          layout="vertical"
        >
          <Form.Item label="当前用户">
            <Input value={selectedUser?.email} disabled />
          </Form.Item>
          <Form.Item label="当前余额（人民币）">
            <Input value={`¥${parseFloat(selectedUser?.balance || 0).toFixed(6)}`} disabled />
          </Form.Item>
          <Form.Item
            label="调整金额（人民币）"
            name="amount"
            rules={[{ required: true, message: '请输入调整金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="正数为增加，负数为减少"
              step={0.000001}
              precision={6}
            />
          </Form.Item>
          <Form.Item
            label="调整原因"
            name="reason"
            rules={[{ required: true, whitespace: true, message: '请输入调整原因' }, { max: 255, message: '调整原因最多255个字符' }]}
          >
            <Input.TextArea rows={3} maxLength={255} showCount placeholder="请输入调整原因" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认调整
              </Button>
              <Button onClick={() => setBalanceModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 用户备用模型配置对话框 */}
      <Modal
        title="用户备用模型配置"
        open={fallbackModalVisible}
        onCancel={() => setFallbackModalVisible(false)}
        width={800}
        footer={null}
      >
        <Alert
          message="配置说明"
          description={
            <div>
              <p>1. <strong>用户级配置</strong>：该用户专属的备用模型列表，优先级最高</p>
              <p>2. <strong>模型级配置</strong>：在模型管理中设置的备用模型</p>
              <p>3. <strong>平台默认配置</strong>：智能路由中的备用模型配置，兜底使用</p>
              <p style={{ color: '#1677ff' }}>优先级顺序：用户级 &gt; 模型级 &gt; 平台默认</p>
            </div>
          }
          type="info"
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 16 }}>
          <p><strong>当前用户：</strong>{selectedUser?.email}</p>
          <p><strong>用户ID：</strong>{selectedUser?.id}</p>
        </div>

        {fallbackLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>加载中...</div>
        ) : (
          <>
            {/* 模型类型选择标签页 */}
            <div style={{ marginBottom: 16 }}>
              <span style={{ marginRight: 8, fontWeight: 500 }}>选择模型类型：</span>
              <Select
                value={selectedModelType}
                onChange={setSelectedModelType}
                style={{ width: 200 }}
              >
                <Select.Option value="text">Text / 对话</Select.Option>
                <Select.Option value="image">Image / 图像</Select.Option>
                <Select.Option value="embeddings">Embeddings / 嵌入</Select.Option>
                <Select.Option value="audio">Audio / 语音</Select.Option>
                <Select.Option value="video">Video / 视频</Select.Option>
                <Select.Option value="rerank">Rerank / 重排序</Select.Option>
                <Select.Option value="speech">Speech / 语音合成</Select.Option>
                <Select.Option value="transcription">Transcription / 语音识别</Select.Option>
              </Select>
            </div>

            {/* 根据选择的类型显示对应的备用模型配置 */}
            {selectedModelType === 'text' && (
              <div style={{ marginBottom: 16 }}>
                <h4>Text / 对话 备用模型</h4>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="选择或输入备用模型名称"
                  value={fallbackModels.text || []}
                  onChange={(value) => setFallbackModels({ ...fallbackModels, text: value })}
                >
                  {availableModels.filter(m => m.type === 'text' && m.status === 'active').map(m => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name}
                    </Select.Option>
                  ))}
                </Select>
                <p style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                  当对话模型失败时，依次尝试这些备用模型
                </p>
              </div>
            )}

            {selectedModelType === 'image' && (
              <div style={{ marginBottom: 16 }}>
                <h4>Image / 图像 备用模型</h4>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="选择或输入备用模型名称"
                  value={fallbackModels.image || []}
                  onChange={(value) => setFallbackModels({ ...fallbackModels, image: value })}
                >
                  {availableModels.filter(m => m.type === 'image' && m.status === 'active').map(m => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {selectedModelType === 'embeddings' && (
              <div style={{ marginBottom: 16 }}>
                <h4>Embeddings / 嵌入 备用模型</h4>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="选择或输入备用模型名称"
                  value={fallbackModels.embeddings || []}
                  onChange={(value) => setFallbackModels({ ...fallbackModels, embeddings: value })}
                >
                  {availableModels.filter(m => m.type === 'embeddings' && m.status === 'active').map(m => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {selectedModelType === 'audio' && (
              <div style={{ marginBottom: 16 }}>
                <h4>Audio / 语音 备用模型</h4>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="选择或输入备用模型名称"
                  value={fallbackModels.audio || []}
                  onChange={(value) => setFallbackModels({ ...fallbackModels, audio: value })}
                >
                  {availableModels.filter(m => m.type === 'audio' && m.status === 'active').map(m => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {selectedModelType === 'video' && (
              <div style={{ marginBottom: 16 }}>
                <h4>Video / 视频 备用模型</h4>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="选择或输入备用模型名称"
                  value={fallbackModels.video || []}
                  onChange={(value) => setFallbackModels({ ...fallbackModels, video: value })}
                >
                  {availableModels.filter(m => m.type === 'video' && m.status === 'active').map(m => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {selectedModelType === 'rerank' && (
              <div style={{ marginBottom: 16 }}>
                <h4>Rerank / 重排序 备用模型</h4>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="选择或输入备用模型名称"
                  value={fallbackModels.rerank || []}
                  onChange={(value) => setFallbackModels({ ...fallbackModels, rerank: value })}
                >
                  {availableModels.filter(m => m.type === 'rerank' && m.status === 'active').map(m => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {selectedModelType === 'speech' && (
              <div style={{ marginBottom: 16 }}>
                <h4>Speech / 语音合成 备用模型</h4>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="选择或输入备用模型名称"
                  value={fallbackModels.speech || []}
                  onChange={(value) => setFallbackModels({ ...fallbackModels, speech: value })}
                >
                  {availableModels.filter(m => m.type === 'speech' && m.status === 'active').map(m => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {selectedModelType === 'transcription' && (
              <div style={{ marginBottom: 16 }}>
                <h4>Transcription / 语音识别 备用模型</h4>
                <Select
                  mode="tags"
                  style={{ width: '100%' }}
                  placeholder="选择或输入备用模型名称"
                  value={fallbackModels.transcription || []}
                  onChange={(value) => setFallbackModels({ ...fallbackModels, transcription: value })}
                >
                  {availableModels.filter(m => m.type === 'transcription' && m.status === 'active').map(m => (
                    <Select.Option key={m.model_name} value={m.model_name}>
                      {m.display_name || m.model_name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {/* 当前配置预览 */}
            <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
              <p style={{ marginBottom: 8, fontWeight: 500 }}>当前配置预览：</p>
              {Object.entries(fallbackModels).filter(([_, v]) => v && v.length > 0).map(([type, models]) => (
                <div key={type} style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 500 }}>{type}: </span>
                  <span>{models.join(', ')}</span>
                </div>
              ))}
              {Object.values(fallbackModels).every(v => !v || v.length === 0) && (
                <span style={{ color: '#888' }}>暂无配置</span>
              )}
            </div>

            <div style={{ marginTop: 24, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleClearFallback}>
                  清除配置（使用平台默认）
                </Button>
                <Button type="primary" onClick={handleSaveFallback}>
                  保存配置
                </Button>
              </Space>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Users;
